/**
 * IQ-Service-Inventory API-Naht — BFF-Proxy zur Facade Admin API (Spec 344, Draft v0.9).
 *
 * list/get/create/update/archive proxyen ueber den merchant-geschachtelten Admin-BFF
 * (`/v1/admin/merchants/{merchantId}/iq-services`), der `X-Admin-Key` serverseitig
 * setzt. Der BFF liefert/erwartet camelCase. Zweite (von zwei) Mapping-Naht-Stellen
 * fuer den Draft->Final-Sync (die erste ist der BFF-Mapper).
 *
 * ⚠ NICHT der platform-`iq-services`-Client (lib/api/iq-services.ts, Gateway-Config).
 */
import { get, post, put, del } from './client';
import type {
  CreateIqServiceInventoryInput,
  IqServiceInventoryFilters,
  IqServiceInventoryItem,
  IqServiceInventoryListPage,
  PlatformIqInventoryResponse,
  PlatformIqInventoryRow,
  UpdateIqServiceInventoryInput,
} from '@/types/iq-service-inventory';

function collectionPath(merchantId: string): string {
  return `/admin/merchants/${encodeURIComponent(merchantId)}/iq-services`;
}

/** Flat, cross-merchant aggregate path (Spec 347 §8) — distinct from the merchant-nested collection. */
const PLATFORM_OVERVIEW_PATH = '/admin/iq-service-inventory';

function resourcePath(merchantId: string, iqServiceKey: string): string {
  return `${collectionPath(merchantId)}/${encodeURIComponent(iqServiceKey)}`;
}

// ============================================================
// Mapping-Naht (Roh-BFF-camelCase -> UI-Typ). Zweite (von zwei) Stellen, die der
// Draft->Final-Sync gegen Facade-Inc-1 beruehrt; haelt UI-Code von BFF-Feldnamen-
// Drift fern. Heute weitgehend identitaetsnah — bewusst zentralisiert.
// ============================================================

/** Roh-Shape, wie der BFF die Liste liefert (Envelope mit `iqServices`). */
interface RawIqServiceInventoryListResponse {
  iqServices: IqServiceInventoryItem[];
  total: number;
  nextCursor: string | null;
}

/** Einzel-Eintrag-Naht: einzige Stelle fuer ein BFF->UI-Feld-Remapping. */
function toItem(raw: IqServiceInventoryItem): IqServiceInventoryItem {
  return raw;
}

/** Envelope-Naht: BFF `iqServices` -> UI `items`; Defaults fuer total/nextCursor. */
function toListPage(raw: RawIqServiceInventoryListResponse): IqServiceInventoryListPage {
  return {
    items: (raw.iqServices ?? []).map(toItem),
    total: raw.total ?? 0,
    nextCursor: raw.nextCursor ?? null,
  };
}

/**
 * One page of a merchant's inventory. Filters are sent as query params (server-side
 * AND); `cursor` continues a previous page. Returns the UI page shape
 * (`items` + `total` + `nextCursor`) so callers can drive "load more".
 */
export async function listIqServiceInventory(
  merchantId: string,
  filters?: IqServiceInventoryFilters,
  cursor?: string,
): Promise<IqServiceInventoryListPage> {
  const params: Record<string, string> = {};
  if (filters?.status) params.status = filters.status;
  if (filters?.flowType) params.flowType = filters.flowType;
  if (filters?.bookingType) params.bookingType = filters.bookingType;
  if (filters?.limit !== undefined) params.limit = String(filters.limit);
  if (cursor) params.cursor = cursor;
  const raw = await get<RawIqServiceInventoryListResponse>(
    collectionPath(merchantId),
    Object.keys(params).length > 0 ? params : undefined,
  );
  return toListPage(raw);
}

export async function getIqServiceInventoryItem(
  merchantId: string,
  iqServiceKey: string,
): Promise<IqServiceInventoryItem> {
  return toItem(await get<IqServiceInventoryItem>(resourcePath(merchantId, iqServiceKey)));
}

export async function createIqServiceInventoryItem(
  merchantId: string,
  input: CreateIqServiceInventoryInput,
): Promise<IqServiceInventoryItem> {
  return toItem(await post<IqServiceInventoryItem>(collectionPath(merchantId), input));
}

export async function updateIqServiceInventoryItem(
  merchantId: string,
  iqServiceKey: string,
  input: UpdateIqServiceInventoryInput,
): Promise<IqServiceInventoryItem> {
  return toItem(await put<IqServiceInventoryItem>(resourcePath(merchantId, iqServiceKey), input));
}

/** Soft-archive (UC-4). 204 No Content — idempotent. */
export async function archiveIqServiceInventoryItem(
  merchantId: string,
  iqServiceKey: string,
): Promise<void> {
  await del<void>(resourcePath(merchantId, iqServiceKey));
}

// ============================================================
// Plattform-Uebersicht (Spec 347 §8) — Cross-Merchant-Aggregat, platform-only.
// Eigene Mapping-Naht im selben Stil wie toItem/toListPage; heute identitaetsnah,
// Defaults fuer rows/failedMerchants, damit eine duenne BFF-Antwort die Seite nicht
// crashed. Single source fuer einen etwaigen Draft->Final-Feldnamen-Drift.
// ============================================================

/** Naht: rohe BFF-Aggregat-Antwort -> UI-Response (Defaults fuer rows/failedMerchants). */
function toOverview(raw: PlatformIqInventoryResponse): PlatformIqInventoryResponse {
  return {
    rows: (raw.rows ?? []).map((row: PlatformIqInventoryRow) => row),
    failedMerchants: raw.failedMerchants ?? [],
    usageUnavailable: raw.usageUnavailable,
    usageTruncated: raw.usageTruncated,
  };
}

/**
 * Cross-merchant inventory aggregate (Spec 347 §8). Platform-only at the route gate
 * (IQ_SERVICE_INVENTORY_OVERVIEW). The client prepends the `/v1` base, so this hits
 * `GET /v1/admin/iq-service-inventory`.
 */
export async function getPlatformIqServiceInventory(): Promise<PlatformIqInventoryResponse> {
  return toOverview(await get<PlatformIqInventoryResponse>(PLATFORM_OVERVIEW_PATH));
}
